import React, { createContext, useContext, useState } from 'react';
import { PromptVersionControl } from '@Models/types';
import { accountsAPI } from '@Lib/axios/account';
import { useAuth } from './CognitoAuthProvider';
import ValidateEmail from '@Utils/emailValidation';

interface ProfileContextProps {
  isProcessingAha: boolean;
  isProcessingTestRail: boolean;
  ahaKeyStatus: KeyStatus;
  testrailKeyStatus: KeyStatus;
  isSavingAHA: boolean;
  testRailCreds: TestrailCreds;
  isSavingTestrail: boolean;

  //additional jira states
  isProcessingJira: boolean;
  jiraKeyStatus: KeyStatus;
  jiraCreds: JiraCredentials;
  isSavingJira: boolean;
  handleUpdateJira: () => Promise<void>;
  handleJiraVerification: () => Promise<void>;
  setJiraCreds: (jiraCreds: JiraCredentials) => void;

  ahaKey: string;
  setAhaKey: (ahaKey: string) => void;
  setTestRailCreds: (testrailcreds: TestrailCreds) => void;
  handleAHAKeyVerification: () => Promise<void>;
  handleTestrailVerification: () => Promise<void>;
  handleUpdateAHAApiKey: () => Promise<void>;
  handleUpdateTestrail: () => Promise<void>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  getHighestVersion: (promptList: PromptVersionControl[]) => string;
  getNextVersion: (promptList: PromptVersionControl[]) => string;
}

const ProfileContext = createContext<ProfileContextProps | undefined>(
  undefined
);

interface KeyStatus {
  message: string;
  status: 'NONE' | 'ERROR' | 'VERIFIED' | 'SUCCESS';
}

interface TestrailCreds {
  testrail_username: string;
  testrail_api_key: string;
}

//new jira interface
export interface JiraCredentials {
  email: string;
  token: string;
}

export const ProfileContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isProcessingAha, setIsProcessingAha] = useState(false);
  const [isProcessingTestRail, setIsProcessingTestRail] = useState(false);
  //new jira state
  const [isProcessingJira, setIsProcessingJira] = useState(false);

  const [ahaKeyStatus, setAhaKeyStatus] = useState<KeyStatus>({
    message: '',
    status: 'NONE',
  });
  const [testrailKeyStatus, setTestrailKeyStatus] = useState<KeyStatus>({
    message: '',
    status: 'NONE',
  });

  //new jira state
  const [jiraKeyStatus, setJiraKeyStatus] = useState<KeyStatus>({
    message: '',
    status: 'NONE',
  });

  const [isSavingAHA, setIsSavingAHA] = useState(false);
  const [isSavingTestrail, setIsSavingTestrail] = useState(false);
  const [isSavingJira, setIsSavingJira] = useState(false); //new jira state

  const [ahaKey, setAhaKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [testRailCreds, setTestRailCreds] = useState({
    testrail_username: '',
    testrail_api_key: '',
  });
  const [jiraCreds, setJiraCreds] = useState<JiraCredentials>({
    email: '',
    token: '',
  });

  const [verifiedAhaKey, setVerifiedAhaKey] = useState('');
  const [verifiedTestRailCreds, setVerifiedTestRailCreds] = useState({
    testrail_username: '',
    testrail_api_key: '',
  });
  const [verifiedJiraCreds, setVerifiedJiraCreds] = useState<JiraCredentials>({
    email: '',
    token: '',
  });

  const { logout, initializeUser } = useAuth();
  const account = accountsAPI(logout);

  const handleAHAKeyVerification = async () => {
    setIsProcessingAha(true);

    if (ahaKey === '') {
      setAhaKeyStatus({
        message: 'Aha! API Key Changes Required!',
        status: 'ERROR',
      });
    } else if (ahaKey.includes('•')) {
      setAhaKeyStatus({
        message: 'Aha! API Key Changes Required!',
        status: 'ERROR',
      });
    } else {
      await account
        .validateAha(ahaKey)
        .then(() => {
          setAhaKeyStatus({
            message: 'Aha! API Key verified successfully',
            status: 'VERIFIED',
          });
          setVerifiedAhaKey(ahaKey);
        })
        .catch((error) => {
          setAhaKeyStatus({
            message: 'Aha! API Key Validation error',
            status: 'ERROR',
          });
          console.error(error);
        });
    }

    setIsProcessingAha(false);
  };

  const handleTestrailVerification = async () => {
    setIsProcessingTestRail(true);
    const { testrail_username, testrail_api_key } = testRailCreds;

    const emailValidationError = ValidateEmail(testrail_username);
    if (emailValidationError) {
      setTestrailKeyStatus({
        message: emailValidationError.message,
        status: 'ERROR',
      });
    } else if (testrail_api_key === '' || testrail_username === '') {
      setTestrailKeyStatus({
        message: 'TestRail Credentials Required!',
        status: 'ERROR',
      });
    } else if (testrail_api_key.includes('•')) {
      setTestrailKeyStatus({
        message: 'TestRail Credentials Changes Required!',
        status: 'ERROR',
      });
    } else {
      await account
        .validateTestRail(testrail_username, testrail_api_key)
        .then(() => {
          setTestrailKeyStatus({
            message: 'TestRail Credentials verified successfully',
            status: 'VERIFIED',
          });
          setVerifiedTestRailCreds(testRailCreds);
        })
        .catch((error) => {
          console.error(error);
          setTestrailKeyStatus({
            message: 'Validation Error',
            status: 'ERROR',
          });
        });
    }
    setIsProcessingTestRail(false);
  };

  //new jira verification
  const handleJiraVerification = async () => {
    setIsProcessingJira(true);
    const { email, token } = jiraCreds;

    const emailValidationError = ValidateEmail(email);

    if (!email || !token) {
      setJiraKeyStatus({
        message: 'All Jira fields are required.',
        status: 'ERROR',
      });
    } else if (emailValidationError) {
      setJiraKeyStatus({
        message: emailValidationError.message,
        status: 'ERROR',
      });
    } else if (token.includes('•')) {
      setJiraKeyStatus({
        message: 'Jira API token changes required.',
        status: 'ERROR',
      });
    } else {
      //call backend API for verification
      await account
        .validateJira(email, token)
        .then(() => {
          setJiraKeyStatus({
            message: 'Jira Credentials verified successfully',
            status: 'VERIFIED',
          });
          setVerifiedJiraCreds(jiraCreds);
        })
        .catch((error) => {
          console.error(error);
          setJiraKeyStatus({
            message: 'Jira Verification Failed',
            status: 'ERROR',
          });
        });
    }

    setIsProcessingJira(false);
  };

  const handleUpdateAHAApiKey = async () => {
    setIsSavingAHA(true);

    const input = verifiedAhaKey;

    await account
      .updateAHAApiKey(input)
      .then(async () => {
        await initializeUser(true);
        setAhaKeyStatus({
          message: 'AHA Key update Successful',
          status: 'SUCCESS',
        });
      })
      .catch((error) => {
        if (error instanceof Error) {
          console.error(error.message);
        } else {
          console.error('An unknown error occurred');
        }
        setAhaKeyStatus({
          message: 'AHA key update failed',
          status: 'ERROR',
        });
      });

    setIsProcessingAha(false);
    setIsSavingAHA(false);
  };

  const getHighestVersion = (promptList: PromptVersionControl[]): string => {
    return promptList.sort((a, b) => {
      const versionA = a.version.split('.').map(Number);
      const versionB = b.version.split('.').map(Number);

      for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
        if ((versionA[i] || 0) > (versionB[i] || 0)) return -1;
        if ((versionA[i] || 0) < (versionB[i] || 0)) return 1;
      }
      return 0;
    })[0].version;
  };

  const getNextVersion = (promptList: PromptVersionControl[]): string => {
    const highestVersion = getHighestVersion(promptList);
    const [major, minor] = String(highestVersion).split('.').map(Number);
    const updatedVersion = `${major}.${minor + 1}`;
    return updatedVersion;
  };

  const handleUpdateTestrail = async () => {
    setIsSavingTestrail(true);

    const { testrail_username, testrail_api_key } = verifiedTestRailCreds;

    await account
      .updateTestrail(testrail_username, testrail_api_key)
      .then(async () => {
        await initializeUser(true);
        setTestrailKeyStatus({
          message: 'Testrail Credentials update successful!',
          status: 'SUCCESS',
        });
      })
      .catch((error) => {
        if (error instanceof Error) {
          console.error(error.message);
        } else {
          console.error('An unknown error occurred');
        }
        setTestrailKeyStatus({
          message: 'Testrail key update failed!',
          status: 'ERROR',
        });
      });

    setIsSavingTestrail(false);
  };

  //new jira update
  const handleUpdateJira = async () => {
    setIsSavingJira(true);
    const { email, token } = verifiedJiraCreds;

    //call backend to save
    await account
      .updateJira(email, token)
      .then(async () => {
        await initializeUser(true);
        setJiraKeyStatus({
          message: 'Jira Credentials update successful',
          status: 'SUCCESS',
        });
      })
      .catch((error) => {
        console.error(error);
        setJiraKeyStatus({
          message: `Jira update failed: ${error}`,
          status: 'ERROR',
        });
      });
    setIsSavingJira(false);
  };

  return (
    <ProfileContext.Provider
      value={{
        isProcessingAha,
        isProcessingTestRail,
        ahaKeyStatus,
        testrailKeyStatus,
        jiraKeyStatus,
        isSavingAHA,
        isSavingTestrail,
        ahaKey,
        setAhaKey,
        testRailCreds,
        setTestRailCreds,
        handleAHAKeyVerification,
        handleTestrailVerification,
        handleUpdateAHAApiKey,
        handleUpdateTestrail,
        loading,
        setLoading,
        getHighestVersion,
        getNextVersion,
        //additional jira states
        isProcessingJira,
        jiraCreds,
        isSavingJira,
        handleUpdateJira,
        handleJiraVerification,
        setJiraCreds,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfileContext = (): ProfileContextProps => {
  const context = useContext(ProfileContext);
  if (!context)
    throw new Error(
      'useProfileContext must be used within a ProfileCtxProvider'
    );
  return context;
};
